import { Permission } from 'app/types/entities'

type RoleDefinition = {
  name: string
  permissions: Permission[]
}

// Non-SMC Workspace Roles

export const Owner: RoleDefinition = {
  name: `Owner`,
  permissions: [
    `pages:contacts`,
    `pages:pipelines`,
    `pages:tasks`,
    `pages:activities`,
    `pages:analytics`,
    `pages:settings`,
    `pages:inbox`,
    `contacts:qualifications`,
    `contacts:attributions`,
    `contacts:merge`,
    `contacts:mark-non-patient`,
    `contacts:lead-status`,
    `settings:budget`,
    `marketing:spend`,
    `staff:performance:own`,
    `staff:performance:others`,
    `settings:integrations`,
    `settings:workspace`,
    `settings:location`,
    `settings:mapping:view`,
    `settings:mapping:edit`,
    `settings:tags:view`,
    `settings:tags:edit`,
    `settings:scheduling`,
    `roles:manage`,
    `staff:manage`,
    `users:manage`,
    `lists:delete-shared`,
  ],
}

export const Admin: RoleDefinition = {
  name: `Admin`,
  permissions: [
    `pages:contacts`,
    `pages:pipelines`,
    `pages:tasks`,
    `pages:activities`,
    `pages:analytics`,
    `pages:settings`,
    `pages:inbox`,
    `contacts:qualifications`,
    `contacts:attributions`,
    `contacts:merge`,
    `contacts:mark-non-patient`,
    `contacts:lead-status`,
    `settings:budget`,
    `staff:performance:own`,
    `staff:performance:others`,
    `settings:integrations`,
    `settings:tags:view`,
    `settings:tags:edit`,
    `settings:scheduling`,
    `settings:workspace`,
    `settings:location`,
    `staff:manage`,
    `users:manage`,
    `lists:delete-shared`,
  ],
}

export const User: RoleDefinition = {
  name: `User`,
  permissions: [
    `pages:contacts`,
    `pages:pipelines`,
    `pages:tasks`,
    `pages:activities`,
    `pages:settings`,
    `pages:inbox`,
    `contacts:qualifications`,
    `contacts:attributions`,
    `contacts:merge`,
    `contacts:mark-non-patient`,
    `contacts:lead-status`,
    `settings:budget`,
    `staff:performance:own`,
    `staff:performance:others`,
    `settings:tags:view`,
    `settings:tags:edit`,
  ],
}

// SMC Workspace Roles

export const SMCOwner: RoleDefinition = {
  name: `Owner`,
  permissions: [
    `pages:contacts`,
    `pages:pipelines`,
    `pages:tasks`,
    `pages:activities`,
    `pages:analytics`,
    `pages:settings`,
    `pages:inbox`,
    `contacts:qualifications`,
    `contacts:attributions`,
    `contacts:merge`,
    `contacts:mark-non-patient`,
    `contacts:lead-status`,
    `staff:performance:own`,
    `staff:performance:others`,
    `settings:workspace`,
    `settings:location`,
    `settings:mapping:edit`,
    `settings:tags:view`,
    `settings:tags:edit`,
    `settings:scheduling`,
    `roles:manage`,
    `staff:manage`,
    `users:manage`,
    `lists:delete-shared`,
  ],
}

export const SMCAdmin: RoleDefinition = {
  name: `Admin`,
  permissions: [
    `pages:contacts`,
    `pages:pipelines`,
    `pages:tasks`,
    `pages:activities`,
    `pages:analytics`,
    `pages:settings`,
    `pages:inbox`,
    `contacts:qualifications`,
    `contacts:attributions`,
    `contacts:merge`,
    `contacts:mark-non-patient`,
    `contacts:lead-status`,
    `staff:performance:own`,
    `staff:performance:others`,
    `settings:tags:view`,
    `settings:tags:edit`,
    `settings:scheduling`,
    `staff:manage`,
    `users:manage`,
    `lists:delete-shared`,
  ],
}

export const SMCUser: RoleDefinition = {
  name: `User`,
  permissions: [
    `pages:contacts`,
    `pages:pipelines`,
    `pages:tasks`,
    `pages:activities`,
    `pages:analytics`,
    `pages:settings`,
    `pages:inbox`,
    `contacts:attributions`,
    `contacts:merge`,
    `contacts:mark-non-patient`,
    `contacts:lead-status`,
    `staff:performance:own`,
    `settings:tags:view`,
    `settings:tags:edit`,
  ],
}

// Non-SMC Org Roles

export const OrgOwner: RoleDefinition = {
  name: `Org Owner`,
  permissions: [
    // Pages (all org pages)
    `org:pages:companies`,
    `org:pages:contacts`,
    `org:pages:activities`,
    `org:pages:pipelines`,
    `org:pages:tasks`,
    `org:pages:inbox`,
    `org:pages:settings`,
    `org:pages:analytics:marketing`,
    `org:pages:analytics:attribution`, // Owner-only
    `org:pages:analytics:new-patients`,
    `org:pages:analytics:revenue`, // Owner-only
    // Features
    `org:create:workspace`,
    `org:create:location`,
    `org:contacts:edit-channel`,
    `org:contacts:edit-qualification`,
    `org:contacts:edit-lead-status`,
    `org:contacts:set-non-patient`,
    `org:settings:see-integrations`,
    `org:settings:manage-users`,
    `org:settings:manage-roles`,
    // Sensitive (Org Owner Only)
    `org:marketing:advanced-filters`, // Owner-only
    `org:marketing:see-spend`, // Owner-only (see budget/spend)
    `org:contacts:see-utm`, // Owner-only
  ],
}

export const OrgAdmin: RoleDefinition = {
  name: `Org Admin`,
  permissions: [
    `org:pages:companies`,
    `org:pages:contacts`,
    `org:pages:activities`,
    `org:pages:pipelines`,
    `org:pages:tasks`,
    `org:pages:inbox`,
    `org:pages:settings`,
    `org:pages:analytics:marketing`,
    `org:pages:analytics:new-patients`,
    `org:create:workspace`,
    `org:create:location`,
    `org:contacts:edit-channel`,
    `org:contacts:edit-qualification`,
    `org:contacts:edit-lead-status`,
    `org:contacts:set-non-patient`,
    `org:settings:see-integrations`,
    `org:settings:manage-users`,
    `org:settings:manage-roles`,
  ],
}

export const OrgUser: RoleDefinition = {
  name: `Org User`,
  permissions: [
    `org:pages:companies`,
    `org:pages:contacts`,
    `org:pages:activities`,
    `org:pages:pipelines`,
    `org:pages:tasks`,
    `org:pages:inbox`,
    `org:pages:settings`,
    `org:pages:analytics:marketing`,
    `org:pages:analytics:new-patients`,
    `org:settings:manage-roles`,
  ],
}

// SMC Org Roles

export const SMCOrgOwner: RoleDefinition = {
  name: `Org Owner`,
  permissions: [
    `org:pages:companies`,
    `org:pages:contacts`,
    `org:pages:activities`,
    `org:pages:pipelines`,
    `org:pages:tasks`,
    `org:pages:inbox`,
    `org:pages:settings`,
    `org:pages:analytics:marketing`,
    `org:pages:analytics:new-patients`,
    `org:pages:analytics:attribution`,
    `org:pages:analytics:revenue`,
    `org:create:workspace`,
    `org:create:location`,
    `org:contacts:see-utm`,
    `org:contacts:edit-channel`,
    `org:contacts:edit-qualification`,
    `org:contacts:edit-lead-status`,
    `org:contacts:set-non-patient`,
    `org:marketing:see-spend`,
    `org:marketing:advanced-filters`,
    `org:settings:see-integrations`,
    `org:settings:manage-users`,
    `org:settings:manage-roles`,
  ],
}

export const SMCOrgAdmin: RoleDefinition = {
  name: `Org Admin`,
  permissions: [
    `org:pages:companies`,
    `org:pages:contacts`,
    `org:pages:activities`,
    `org:pages:pipelines`,
    `org:pages:tasks`,
    `org:pages:inbox`,
    `org:pages:settings`,
    `org:pages:analytics:marketing`,
    `org:pages:analytics:new-patients`,
    `org:create:workspace`,
    `org:create:location`,
    `org:contacts:edit-channel`,
    `org:contacts:edit-qualification`,
    `org:contacts:edit-lead-status`,
    `org:contacts:set-non-patient`,
    `org:marketing:see-spend`,
    `org:settings:see-integrations`,
    `org:settings:manage-users`,
  ],
}

export const SMCOrgUser: RoleDefinition = {
  name: `Org User`,
  permissions: [
    `org:pages:companies`,
    `org:pages:contacts`,
    `org:pages:activities`,
    `org:pages:pipelines`,
    `org:pages:tasks`,
    `org:pages:inbox`,
    `org:pages:settings`,
    `org:pages:analytics:marketing`,
    `org:pages:analytics:new-patients`,
    `org:settings:manage-users`,
  ],
}

export const WORKSPACE_ROLES = [Owner, Admin, User]
export const SMC_WORKSPACE_ROLES = [SMCOwner, SMCAdmin, SMCUser]

export const ORG_ROLES = [OrgOwner, OrgAdmin, OrgUser]
export const SMC_ORG_ROLES = [SMCOrgOwner, SMCOrgAdmin, SMCOrgUser]
